import { format } from 'd3-format';
import { FluentBundle } from 'fluent';
import { GetString } from 'fluent-react';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { PolicyRecommendation } from '../../graphQL/types/shared';
import { getLinearScale } from '../../NonConstantUtils';
import {
  BottomLegendNegative,
  BottomLegendPositive,
  getBottomLegendContainer,
  getLeftLegendContainer,
  getXAxisLabelContainer,
  getYAxisLabelContainer,
  LeftLegendItemNegative,
  LeftLegendItemPositive,
} from '../exportGrowthDynamicsChart/axisElementsSharedStyles';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Localized from '../landing/Localized';
import Circle, {
  Type,
} from './Circle';
import {
  ChartContainer as ChartContainerBase,
  XLegendContainer as BottomLegendContainerBase,
  XTicksContainer as XTicksContainerBase,
  XxisLabelContainer as XAxisLabelContainerBase,
  YAxisLabelContainer as YAxisLabelContainerBase,
  YLegendContainer as LeftLegendContainerBase,
  YTicksContainer as YTicksContainerBase,
} from './Grid';
import QuadrantLabel from './QuadrantLabel';
import {
  highlightedNodeRadius,
  policyColors,
} from './Utils';

//#region Styling
const SVG = styled.svg`
  position: relative;
`;
const ChartContainer = styled(ChartContainerBase)`
  border: 1px solid rgba(0, 0, 0, 0.4);
  position: relative;
`;
const GridLinesContainer = styled(ChartContainerBase)`
  position: relative;
`;
const TooltipsContainer = styled(ChartContainerBase)`
  position: relative;
  pointer-events: none;
`;
const tooltipArrowHeight = 6; // in px
const tooltipArrowHalfBase = 6; // in px
const tooltipBackgroundColor = 'white';
const tooltipVerticalSeparationFromNodeBorder = 3; // in px
const Tooltip = styled.div`
  position: absolute;
  transform: translateX(-50%) translateY(calc(-100% - ${tooltipVerticalSeparationFromNodeBorder}px));
  font-size: 0.875rem;
  border-width: 1px;
  border-style: solid;
  padding: 5px;
  line-height: 1.2;
  background-color: white;
  border-color: var(--border-color);

  &::after,
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-left-style: solid;
    border-left-color: transparent;
    border-right-style: solid;
    border-right-color: transparent;
    border-top-style: solid;
  }

  /* Arrow fill */
  &::after {
    border-top-width: ${tooltipArrowHeight}px;
    border-top-color: ${tooltipBackgroundColor};
    border-left-width: ${tooltipArrowHalfBase}px;
    border-right-width: ${tooltipArrowHalfBase}px;
  }

  /* Arrow stroke */
  &::before {
    border-top-width: ${tooltipArrowHeight + 1}px;
    border-top-color: var(--border-color);
    border-left-width: ${tooltipArrowHalfBase + 1}px;
    border-right-width: ${tooltipArrowHalfBase + 1}px;
  }
`;
const XTicksContainer = styled(XTicksContainerBase)`
  position: relative;
  pointer-events: none;
`;
const TickLabel = styled.div`
  position: absolute;
  font-size: 0.75rem;
  color: #666;
`;
const XTickLabel = styled(TickLabel)`
  top: 0;
  transform: translateX(-50%);
`;
const YTicksContainer = styled(YTicksContainerBase)`
  position: relative;
  pointer-events: none;
`;
const YTickLabel = styled(TickLabel)`
  right: 5px;
  transform: translateY(-50%);
`;
const QuadrantLabelsContainer = styled(ChartContainerBase)`
  position: relative;
  pointer-events: none;
`;
const LeftLegendContainer = getLeftLegendContainer(LeftLegendContainerBase);
const BottomLegendContainer = getBottomLegendContainer(BottomLegendContainerBase);
const YAxisLabelContainer = getYAxisLabelContainer(YAxisLabelContainerBase);
const XAxisLabelContainer = styled(getXAxisLabelContainer(XAxisLabelContainerBase))`
  align-items: flex-end;
`;
//#endregion

// Allow CSS custom properties
declare module 'csstype' {
  interface Properties {
    '--border-color'?: string;
  }
}

const circleAnimationDuration = 1000;

export interface Datum {
  id: string;
  label: string;
  eci: number;
  coi: number;
  policy: PolicyRecommendation;
}

interface Size {
  width: number;
  height: number;
}

interface Props {
  data: Datum[];
  highlighted: string;
  getFluentString: GetString;
  countryName: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  fluentBundle: FluentBundle;
  getZIndex: (value: string) => number;
  openFlowChart: (value: PolicyRecommendation | null) => void;
}

const Chart = (props: Props) => {
  const {
    data, highlighted, getFluentString,
    countryName, minX, maxX, minY, maxY,
    fluentBundle, getZIndex, openFlowChart,
  } = props;

  const chartRootElRef = useRef<HTMLDivElement | null>(null);
  const [chartAreaSize, setChartAreaSize] = useState<Size | undefined>(undefined);
  const [hovered, setHovered] = useState<string | undefined>(undefined);

  useEffect(() => {
    const {current: el} = chartRootElRef;
    if (el !== null) {
      const {width, height} = el.getBoundingClientRect();
      setChartAreaSize({
        width: Math.floor(width),
        height: Math.floor(height),
      });
    }
  }, []);

  let chartElem: React.ReactElement<any> | null;
  let xTicksElem: React.ReactElement<any> | null;
  let yTicksElem: React.ReactElement<any> | null;
  let tooltipsElem: React.ReactElement<any> | null;
  let gridlines: React.ReactElement<any> | null;
  let quadrantLabels: React.ReactElement<any> | null;
  if (chartAreaSize === undefined) {
    chartElem = null;
    xTicksElem = null;
    yTicksElem = null;
    tooltipsElem = null;
    gridlines = null;
    quadrantLabels = null;
  } else {
    const {width: fullWidth, height: fullHeight} = chartAreaSize;
    if (minX === undefined || maxX === undefined ||
        minY === undefined || maxY === undefined) {
      throw new Error('Most likely the data array is empty');
    }
    const width = fullWidth - 2 * highlightedNodeRadius;
    const height = fullHeight - 2 * highlightedNodeRadius;
    const xScale = getLinearScale([minX, maxX], [0, width]);
    const yScale = getLinearScale([minY, maxY], [height, 0]);

    const formatTickLabel = format('2~');
    const xTickValues = xScale.ticks();
    const xTickLabels = xTickValues.map(value => (
      <XTickLabel key={`tick-label-${value}`} style={{left: `${xScale(value)}px`}}>
        {formatTickLabel(value)}
      </XTickLabel>
    ));
    xTicksElem = (
      <XTicksContainer style={{zIndex: getZIndex('xTicksContainer')}}>
        {xTickLabels}
      </XTicksContainer>
    );

    const yTickValues = yScale.ticks();
    const yTickLabels = yTickValues.map(value => (
      <YTickLabel  key={`tick-label-${value}`} style={{top: `${yScale(value)}px`}}>
        {formatTickLabel(value)}
      </YTickLabel>
    ));
    yTicksElem = (
      <YTicksContainer style={{zIndex: getZIndex('yTicksContainer')}}>
        {yTickLabels}
      </YTicksContainer>
    );

    const actualHovered = (hovered === highlighted) ? undefined : hovered;

    const onMouseEnterNode = (id: string) => setHovered(id);
    const onMouseLeaveNode = () => setHovered(undefined);

    const circles: Array<React.ReactElement<any>> = [];
    for (const {id, eci, coi, policy} of data) {
      const type = id === actualHovered ? Type.Hovered : Type.Normal;
      const elem = (
        <Circle
          id={id}
          x={xScale(eci)}
          y={yScale(coi)}
          type={id === highlighted ? Type.Highlighted : type}
          policy={policy}
          onMouseEnter={onMouseEnterNode}
          onMouseLeave={onMouseLeaveNode}
          animationDuration={circleAnimationDuration}
          key={`circle-${id}`}
        />
      );
      // Add elements to the beginning and the highlighted
      // element to the end so that it renders above the rest.
      // z-index does not work on svg elements
      if (id !== highlighted) {
        circles.unshift(elem);
      } else {
        circles.push(elem);
      }
    }

    const gridLines = [...yTickValues.map(value => {
      const yCoord = yScale(value);
      return (
        <line
          x1={0} y1={yCoord}
          x2={fullWidth} y2={yCoord}
          stroke='black' strokeOpacity={0.4} strokeDasharray={2}
          key={`grid-line-y-${value}`}
        />
      );
    }), ...xTickValues.map(value => {
      const xCoord = xScale(value);
      return (
        <line
          x1={xCoord} y1={0}
          x2={xCoord} y2={fullHeight}
          stroke='black' strokeOpacity={0.4} strokeDasharray={2}
          key={`grid-line-x-${value}`}
        />
      );
    })];

    chartElem = (
      <SVG width={fullWidth} height={fullHeight}>
        {circles}
      </SVG>
    );

    gridlines = (
      <GridLinesContainer style={{zIndex: getZIndex('gridLinesContainer')}}>
        <SVG width={fullWidth} height={fullHeight}>
          {gridLines}
        </SVG>
      </GridLinesContainer>
    );

    const tooltipToNodeCircumferenceSeparation = 5; // in px
    const nodeCOIToTooltipTopCSSProperty = (nodeCOI: number) => Math.round(
      yScale(nodeCOI) - highlightedNodeRadius - tooltipToNodeCircumferenceSeparation,
    );
    const highlightedDatum = data.find(({id}) => id === highlighted);
    if (highlightedDatum === undefined) {
      throw new Error('Cannot find datum for highlighted country ' + highlighted);
    }
    const highlightTooltipStyle: React.CSSProperties = {
      'left': `${xScale(highlightedDatum.eci)}px`,
      'top': `${nodeCOIToTooltipTopCSSProperty(highlightedDatum.coi)}px`,
      '--border-color':  policyColors[highlightedDatum.policy],
    };
    const highlightTooltip = (
      <Tooltip style={highlightTooltipStyle}>
        {highlightedDatum.label}
      </Tooltip>
    );
    let hoverTooltip: React.ReactElement<any> | null;
    if (actualHovered === undefined) {
      hoverTooltip = null;
    } else {
      const hoveredDatum = data.find(({id}) => id === actualHovered);
      if (hoveredDatum === undefined) {
        throw new Error('Cannot find datum for hovered country ' + actualHovered);
      }
      const hoverTooltipStyle: React.CSSProperties = {
        'left': `${xScale(hoveredDatum.eci)}px`,
        'top': `${nodeCOIToTooltipTopCSSProperty(hoveredDatum.coi)}px`,
        '--border-color': policyColors[hoveredDatum.policy],
      };
      hoverTooltip = (
        <Tooltip style={hoverTooltipStyle}>
          {hoveredDatum.label}
        </Tooltip>
      );
    }

    tooltipsElem = (
      <TooltipsContainer style={{zIndex: getZIndex('tooltipsContainer')}}>
        {highlightTooltip}
        {hoverTooltip}
      </TooltipsContainer>
    );

    quadrantLabels = (
      <QuadrantLabelsContainer style={{zIndex: getZIndex('quadrantLabelsContainer')}}>
        <QuadrantLabel
          policy={PolicyRecommendation.ParsimoniousIndustrial}
          activePolicy={highlightedDatum.policy}
          getFluentString={getFluentString}
          openFlowChart={openFlowChart}
        />
        <QuadrantLabel
          policy={PolicyRecommendation.StrategicBets}
          activePolicy={highlightedDatum.policy}
          getFluentString={getFluentString}
          openFlowChart={openFlowChart}
        />
        <QuadrantLabel
          policy={PolicyRecommendation.LightTouch}
          activePolicy={highlightedDatum.policy}
          getFluentString={getFluentString}
          openFlowChart={openFlowChart}
        />
        <QuadrantLabel
          policy={PolicyRecommendation.TechFrontier}
          activePolicy={highlightedDatum.policy}
          getFluentString={getFluentString}
          openFlowChart={openFlowChart}
        />
      </QuadrantLabelsContainer>
    );
  }

  const leftLegendPositive = getFluentString('rock-song-left-legend-positive');
  const leftLegendNegative = getFluentString('rock-song-left-legend-negative');
  const bottomLegendPositive = getFluentString('rock-song-bottom-legend-positive');
  const bottomLegendNegative = getFluentString('rock-song-bottom-legend-negative');

  const xAxisLabel = getFluentString('rock-song-x-axis-label', {
    'country-name': countryName,
  });

  const yAxisLabelArgs = {
    'country-name': countryName,
    'COIGlossaryTerm': (
      <GlossaryTerm
        type={TooltipType.Standard}
        term={getFluentString('glossary-coi-term-name')}
        explanation={getFluentString('glossary-complexity-outlook-index-term-definition')}
        citation={undefined}
        align={TooltipAlign.YAxis}
        attachment={null}
      />
    ),
  };
  const yAxisLabel = (
    <Localized
      bundle={fluentBundle}
      messageId='rock-song-y-axis-label'
      messageArgs={yAxisLabelArgs}
    >
      <div />
    </Localized>
  );

  return (
    <>
      <LeftLegendContainer style={{zIndex: getZIndex('leftLegendContainer')}}>
        <LeftLegendItemNegative>{leftLegendNegative}</LeftLegendItemNegative>
        <LeftLegendItemPositive>{leftLegendPositive}</LeftLegendItemPositive>
      </LeftLegendContainer>
      <BottomLegendContainer style={{zIndex: getZIndex('bottomLegendContainer')}}>
        <BottomLegendNegative> {bottomLegendNegative} </BottomLegendNegative>
        <BottomLegendPositive> {bottomLegendPositive} </BottomLegendPositive>
      </BottomLegendContainer>
      <YAxisLabelContainer style={{zIndex: getZIndex('yAxisLabelContainer')}}>
        {yAxisLabel}
      </YAxisLabelContainer>
      <XAxisLabelContainer style={{zIndex: getZIndex('xAxisLabelContainer')}}>
        {xAxisLabel}
      </XAxisLabelContainer>
      {xTicksElem}
      {yTicksElem}
      {gridlines}
      <ChartContainer
        ref={chartRootElRef}
        style={{zIndex: getZIndex('chartContainer')}}
      >
        {chartElem}
      </ChartContainer>
      {quadrantLabels}
      {tooltipsElem}
    </>
  );
};

export default Chart;
